// Agenda — the coordinator's scheduling backoffice. Two tabs:
//   • Doctores — the roster (add / edit / activate) + a per-doctor weekly
//     availability editor (weekly hours + consultation length + time-off).
//   • Citas — upcoming appointments booked by the agent or a coordinator.
// Composes the locked design system; hardcoded es-MX copy per the pages
// convention.

import { useQuery, useQueryClient } from '@tanstack/react-query'
import { HEALTHCARE_SPECIALTIES } from '@vysto/shared/compiler'
import { useEffect, useRef, useState } from 'react'

import {
  addTimeOff,
  cancelCita,
  type Cita,
  confirmCita,
  createDoctor,
  deleteTimeOff,
  type Doctor,
  getCitas,
  getDoctores,
  getDoctorSchedule,
  noShowCita,
  saveDoctorSchedule,
  updateDoctor,
} from './agenda-data.js'
import {
  buildWeek,
  type DayBlocks,
  fmtDateTime,
  MultiBlockWeekEditor,
  serializeWeek,
} from './schedule-editor.js'
import { useSession } from '../../auth/session.js'
import {
  Button,
  Chip,
  DataTable,
  type DataTableColumn,
  EmptyStateCard,
  FormField,
  Input,
  Modal,
  NumberInput,
  PageHeader,
  Select,
  Spinner,
  Tabs,
  TabPanel,
  toast,
} from '../../design-system/index.js'
import { QueryError } from '../../lib/QueryError.js'
import { foldForSearch } from '../../lib/search.js'

/** LAS SUGERENCIAS DEL ALTA DE UN DOCTOR (D21-4).
 *
 *  Desde D21-4 esta pantalla es la FUENTE: el dato "Especialidad" de una clínica
 *  toma sus opciones del padrón de médicos activos, así que lo que se teclea
 *  aquí es lo que el Agente podrá ofrecer. Y una fuente se fragmenta en el punto
 *  de entrada: 'Cardiologo' escrito hoy y 'Cardiología' escrito el mes que viene
 *  son DOS especialidades para el padrón, y el paciente que pide cardiología
 *  acaba eligiendo entre dos renglones que dicen lo mismo.
 *
 *  Un `datalist` es la respuesta barata: SUGIERE sin obligar. El campo sigue
 *  siendo texto libre — una clínica escribe lo que su médico es, y la lista de
 *  arranque no tiene por qué ser la suya —, pero lo que ya está capturado y las
 *  siete de arranque salen a la mano.
 *
 *  Las del PADRÓN van primero: son las del cliente, y ganan a las de fábrica.
 *  El de-duplicado es por plegado (sin acentos, sin mayúsculas) para no ofrecer
 *  dos renglones que se leen igual, y conserva la grafía CAPTURADA, que es la
 *  misma regla que `options-source.ts` corre del otro lado. */
function specialtySuggestions(roster: readonly Doctor[]): string[] {
  const byFolded = new Map<string, string>()
  for (const value of [...roster.map((d) => d.specialty), ...HEALTHCARE_SPECIALTIES]) {
    const trimmed = value.trim()
    if (trimmed === '') continue
    const key = foldForSearch(trimmed)
    if (!byFolded.has(key)) byFolded.set(key, trimmed)
  }
  return [...byFolded.values()]
}

const TABS = [
  { id: 'doctores', label: 'Doctores' },
  { id: 'citas', label: 'Citas' },
]

export function AgendaPage(): JSX.Element {
  const [tab, setTab] = useState('doctores')
  const session = useSession()
  // Read-only roles (tenant_viewer / tenant_agent) see the agenda but not the
  // write controls, which the API gates with requireManage and 403s. Hide them
  // instead of surfacing an error toast on click. UI hint only; the API still
  // enforces every write.
  const canManage = session?.canManageWorkspace ?? false
  return (
    <div data-testid="agenda" className="mx-auto w-full max-w-5xl">
      <PageHeader
        title="Agenda"
        subtitle="Administra a tus doctores, sus horarios y las citas agendadas."
      />
      <Tabs
        tabs={TABS}
        activeId={tab}
        onChange={setTab}
        idBase="agenda"
        aria-label="Secciones de agenda"
      />
      <TabPanel idBase="agenda" tabId={tab}>
        {tab === 'doctores' && <DoctoresTab canManage={canManage} />}
        {tab === 'citas' && <CitasTab canManage={canManage} />}
      </TabPanel>
    </div>
  )
}

// ─── Doctores ───────────────────────────────────────────────────────────────
function DoctoresTab({ canManage }: { canManage: boolean }): JSX.Element {
  const qc = useQueryClient()
  const {
    data: doctores,
    isLoading,
    isError,
    refetch,
  } = useQuery({ queryKey: ['doctores'], queryFn: getDoctores })
  const [editing, setEditing] = useState<Doctor | null>(null)
  const [creating, setCreating] = useState(false)
  const [scheduleFor, setScheduleFor] = useState<Doctor | null>(null)

  if (isError)
    return <QueryError message="No pudimos cargar tus doctores." onRetry={() => void refetch()} />
  if (isLoading || !doctores) return <Spinner label="Cargando doctores…" />

  const columns: DataTableColumn<Doctor>[] = [
    { key: 'name', label: 'Doctor', render: (d) => <span className="font-medium">{d.name}</span> },
    { key: 'specialty', label: 'Especialidad', render: (d) => d.specialty },
    { key: 'location', label: 'Sucursal', render: (d) => d.location ?? '—' },
    {
      key: 'status',
      label: 'Estado',
      render: (d) => (
        <Chip tone={d.status === 'active' ? 'ok' : 'draft'} dot>
          {d.status === 'active' ? 'Activo' : 'Inactivo'}
        </Chip>
      ),
    },
    // Manage-only actions column: viewers/agents get a read-only roster.
    ...(canManage
      ? [
          {
            key: 'actions',
            label: '',
            align: 'right' as const,
            render: (d: Doctor) => (
              <div className="flex justify-end gap-2">
                <Button size="sm" variant="secondary" onClick={() => setScheduleFor(d)}>
                  Horarios
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setEditing(d)}>
                  Editar
                </Button>
              </div>
            ),
          },
        ]
      : []),
  ]

  return (
    <div className="flex flex-col gap-4">
      {canManage && (
        <div className="flex justify-end">
          <Button size="sm" onClick={() => setCreating(true)}>
            Nuevo doctor
          </Button>
        </div>
      )}
      {doctores.length === 0 ? (
        <EmptyStateCard
          icon={<span aria-hidden>🩺</span>}
          kind="day1"
          title="Aún no hay doctores"
          body="Agrega a tus médicos y define sus horarios para que el agente pueda ofrecer citas."
          actions={
            canManage ? (
              <Button size="sm" onClick={() => setCreating(true)}>
                Nuevo doctor
              </Button>
            ) : undefined
          }
        />
      ) : (
        <DataTable caption="Doctores" columns={columns} rows={doctores} />
      )}

      {creating && (
        <DoctorModal
          roster={doctores}
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false)
            void qc.invalidateQueries({ queryKey: ['doctores'] })
          }}
        />
      )}
      {editing && (
        <DoctorModal
          doctor={editing}
          roster={doctores}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null)
            void qc.invalidateQueries({ queryKey: ['doctores'] })
          }}
        />
      )}
      {scheduleFor && (
        <AvailabilityModal doctor={scheduleFor} onClose={() => setScheduleFor(null)} />
      )}
    </div>
  )
}

function DoctorModal({
  doctor,
  roster,
  onClose,
  onSaved,
}: {
  doctor?: Doctor
  /** El padrón YA cargado por la pestaña, para las sugerencias de especialidad.
   *  No se vuelve a pedir: es la misma consulta que pinta la tabla de atrás. */
  roster: readonly Doctor[]
  onClose: () => void
  onSaved: () => void
}): JSX.Element {
  const [name, setName] = useState(doctor?.name ?? '')
  const [specialty, setSpecialty] = useState(doctor?.specialty ?? '')
  const [location, setLocation] = useState(doctor?.location ?? '')
  const [status, setStatus] = useState<'active' | 'inactive'>(doctor?.status ?? 'active')
  const [busy, setBusy] = useState(false)
  const isEdit = Boolean(doctor)

  async function submit(): Promise<void> {
    if (!name.trim() || !specialty.trim()) return
    setBusy(true)
    try {
      if (doctor) {
        await updateDoctor(doctor.id, {
          name: name.trim(),
          specialty: specialty.trim(),
          location: location.trim() || null,
          status,
        })
      } else {
        await createDoctor({
          name: name.trim(),
          specialty: specialty.trim(),
          ...(location.trim() ? { location: location.trim() } : {}),
        })
      }
      toast.ok(isEdit ? 'Doctor actualizado' : 'Doctor agregado')
      onSaved()
    } catch {
      toast.error('No se pudo guardar', { description: 'Inténtalo de nuevo.' })
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={isEdit ? 'Editar doctor' : 'Nuevo doctor'}
      subtitle={isEdit ? 'Actualiza los datos del doctor.' : 'Agrega un doctor a tu equipo.'}
      busy={busy}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button className="ml-auto" onClick={() => void submit()} disabled={busy}>
            {busy ? 'Guardando…' : isEdit ? 'Guardar' : 'Agregar'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3.5">
        <FormField label="Nombre" htmlFor="doc-name" required>
          <Input id="doc-name" value={name} onChange={(e) => setName(e.target.value)} />
        </FormField>
        <FormField
          label="Especialidad"
          htmlFor="doc-spec"
          required
          helper="El Agente ofrece estas especialidades cuando pregunta por qué consulta buscan."
        >
          <Input
            id="doc-spec"
            list="doc-spec-sugerencias"
            value={specialty}
            onChange={(e) => setSpecialty(e.target.value)}
          />
        </FormField>
        {/* FUERA del FormField a propósito: ese componente clona a su ÚNICO
            hijo para inyectarle el id, el `required` y el `aria-describedby`, y
            un segundo hijo lo dejaría sin ninguna de las tres. El `<datalist>`
            se ata por su id, así que dónde vive en el árbol le da igual. */}
        <datalist id="doc-spec-sugerencias" data-testid="doc-spec-sugerencias">
          {specialtySuggestions(roster).map((s) => (
            <option key={s} value={s} />
          ))}
        </datalist>
        <FormField label="Sucursal" htmlFor="doc-loc" required="optional">
          <Input id="doc-loc" value={location} onChange={(e) => setLocation(e.target.value)} />
        </FormField>
        {isEdit && (
          <FormField label="Estado" htmlFor="doc-status">
            <Select
              id="doc-status"
              options={[
                { value: 'active', label: 'Activo' },
                { value: 'inactive', label: 'Inactivo' },
              ]}
              value={status}
              onChange={(e) => setStatus(e.target.value as 'active' | 'inactive')}
            />
          </FormField>
        )}
      </div>
    </Modal>
  )
}

function AvailabilityModal({
  doctor,
  onClose,
}: {
  doctor: Doctor
  onClose: () => void
}): JSX.Element {
  const qc = useQueryClient()
  const { data, isLoading } = useQuery({
    queryKey: ['doctor-schedule', doctor.id],
    queryFn: () => getDoctorSchedule(doctor.id),
  })
  const [week, setWeek] = useState<DayBlocks[]>([])
  const [slotMinutes, setSlotMinutes] = useState(30)
  const [busy, setBusy] = useState(false)

  // Seed the week editor ONCE from the initial load. Adding/removing time-off
  // (TimeOffSection) refetches this same query, so re-seeding on every `data`
  // change would silently wipe the user's unsaved weekly-hours edits.
  const seeded = useRef(false)
  useEffect(() => {
    if (data && !seeded.current) {
      seeded.current = true
      setWeek(buildWeek(data.schedules))
      setSlotMinutes(data.schedules[0]?.slotMinutes ?? 30)
    }
  }, [data])

  async function save(): Promise<void> {
    setBusy(true)
    try {
      // Serialize EVERY block of every open day (one row per weekday+from+to).
      // The API models slotMinutes per row, but the UI keeps a single length for
      // the whole week, so apply it to each block.
      await saveDoctorSchedule(doctor.id, serializeWeek(week, slotMinutes))
      toast.ok('Horario guardado')
      void qc.invalidateQueries({ queryKey: ['doctor-schedule', doctor.id] })
      onClose()
    } catch {
      toast.error('No se pudo guardar el horario', { description: 'Inténtalo de nuevo.' })
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={`Horarios de ${doctor.name}`}
      subtitle="Define las horas de atención por día y la duración de cada consulta."
      size="wide"
      busy={busy}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cerrar
          </Button>
          <Button className="ml-auto" onClick={() => void save()} disabled={busy || isLoading}>
            {busy ? 'Guardando…' : 'Guardar horario'}
          </Button>
        </>
      }
    >
      {isLoading || !data ? (
        <Spinner label="Cargando horario…" />
      ) : (
        <div className="flex flex-col gap-5">
          <MultiBlockWeekEditor week={week} onChange={setWeek} />
          <FormField label="Duración de cada consulta (minutos)" htmlFor="slot-min">
            <NumberInput
              id="slot-min"
              value={slotMinutes}
              onChange={setSlotMinutes}
              min={5}
              max={240}
              step={5}
            />
          </FormField>
          <TimeOffSection doctor={doctor} />
        </div>
      )}
    </Modal>
  )
}

function TimeOffSection({ doctor }: { doctor: Doctor }): JSX.Element {
  const qc = useQueryClient()
  const { data } = useQuery({
    queryKey: ['doctor-schedule', doctor.id],
    queryFn: () => getDoctorSchedule(doctor.id),
  })
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [reason, setReason] = useState('')

  const refresh = (): void =>
    void qc.invalidateQueries({ queryKey: ['doctor-schedule', doctor.id] })

  async function add(): Promise<void> {
    if (!from || !to) return
    try {
      await addTimeOff(doctor.id, {
        startsAt: new Date(`${from}T00:00:00`).toISOString(),
        endsAt: new Date(`${to}T23:59:59`).toISOString(),
        ...(reason.trim() ? { reason: reason.trim() } : {}),
      })
      setFrom('')
      setTo('')
      setReason('')
      refresh()
    } catch {
      toast.error('No se pudo bloquear esas fechas')
    }
  }

  async function remove(id: string): Promise<void> {
    try {
      await deleteTimeOff(doctor.id, id)
      refresh()
    } catch {
      toast.error('No se pudo quitar el bloqueo')
    }
  }

  return (
    <div className="flex flex-col gap-2 border-t border-border pt-4">
      <p className="text-sm font-medium">Días bloqueados (vacaciones, ausencias)</p>
      {(data?.timeOff ?? []).length === 0 ? (
        <p className="text-sm text-text-3">Sin bloqueos.</p>
      ) : (
        <ul className="flex flex-col gap-1">
          {data?.timeOff.map((t) => (
            <li key={t.id} className="flex items-center justify-between text-sm">
              <span>
                {fmtDateTime(t.startsAt)} — {fmtDateTime(t.endsAt)}
                {t.reason ? ` · ${t.reason}` : ''}
              </span>
              <Button size="sm" variant="ghost" onClick={() => void remove(t.id)}>
                Quitar
              </Button>
            </li>
          ))}
        </ul>
      )}
      <div className="flex flex-wrap items-end gap-2">
        <FormField label="Desde" htmlFor="to-from">
          <Input id="to-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </FormField>
        <FormField label="Hasta" htmlFor="to-to">
          <Input id="to-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </FormField>
        <FormField label="Motivo" htmlFor="to-reason" required="optional">
          <Input id="to-reason" value={reason} onChange={(e) => setReason(e.target.value)} />
        </FormField>
        <Button size="sm" variant="secondary" onClick={() => void add()} disabled={!from || !to}>
          Bloquear
        </Button>
      </div>
    </div>
  )
}

// ─── Citas ──────────────────────────────────────────────────────────────────
function CitasTab({ canManage }: { canManage: boolean }): JSX.Element {
  const qc = useQueryClient()
  const {
    data: citas,
    isLoading,
    isError,
    refetch,
  } = useQuery({ queryKey: ['citas'], queryFn: getCitas })
  const [busyId, setBusyId] = useState<string | null>(null)
  const [confirmCancel, setConfirmCancel] = useState<Cita | null>(null)

  if (isError)
    return <QueryError message="No pudimos cargar las citas." onRetry={() => void refetch()} />
  if (isLoading || !citas) return <Spinner label="Cargando citas…" />

  if (citas.length === 0) {
    return (
      <EmptyStateCard
        icon={<span aria-hidden>📅</span>}
        kind="day1"
        title="Aún no hay citas"
        body="Cuando el agente o un coordinador agende una cita, aparecerá aquí."
      />
    )
  }

  async function act(id: string, fn: (id: string) => Promise<void>, okMsg: string): Promise<void> {
    setBusyId(id)
    try {
      await fn(id)
      toast.ok(okMsg)
      void qc.invalidateQueries({ queryKey: ['citas'] })
    } catch {
      toast.error('No se pudo completar la acción', { description: 'Inténtalo de nuevo.' })
    } finally {
      setBusyId(null)
    }
  }

  const columns: DataTableColumn<(typeof citas)[number]>[] = [
    {
      key: 'patient',
      label: 'Paciente',
      render: (c) => {
        const cobertura =
          c.coverage === 'aseguradora'
            ? (c.insurer ?? 'Seguro')
            : c.coverage === 'particular'
              ? 'Particular'
              : null
        const intake = [c.reason, c.firstVisit ? 'Primera vez' : null, cobertura].filter(Boolean)
        return (
          <div className="min-w-0">
            <span className="font-medium">{c.patientName}</span>
            {intake.length > 0 && (
              <span className="block truncate text-[11px] text-text-3">{intake.join(' · ')}</span>
            )}
          </div>
        )
      },
    },
    {
      key: 'doctor',
      label: 'Doctor',
      render: (c) => (
        <span>
          {c.doctorName} <span className="text-text-3">· {c.specialty}</span>
        </span>
      ),
    },
    { key: 'when', label: 'Fecha y hora', render: (c) => fmtDateTime(c.startsAt) },
    {
      key: 'status',
      label: 'Estado',
      render: (c) => (
        <Chip tone={c.status === 'confirmed' ? 'ok' : 'warn'} dot>
          {c.status === 'confirmed' ? 'Confirmada' : 'Pendiente'}
        </Chip>
      ),
    },
    // Manage-only actions column: viewers/agents get a read-only list.
    ...(canManage
      ? [
          {
            key: 'actions',
            label: '',
            align: 'right' as const,
            render: (c: (typeof citas)[number]) => {
              // "No llegó" only makes sense once the slot has passed (a no-show
              // is inherently in the past). The window starts at midnight, so a
              // cita earlier today is still listed and can be marked.
              const isPast = new Date(c.startsAt).getTime() < Date.now()
              return (
                <div className="flex justify-end gap-2">
                  {c.status !== 'confirmed' && (
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={busyId === c.id}
                      onClick={() => void act(c.id, confirmCita, 'Cita confirmada')}
                    >
                      Confirmar
                    </Button>
                  )}
                  {isPast && (
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={busyId === c.id}
                      onClick={() => void act(c.id, noShowCita, 'Marcada como inasistencia')}
                    >
                      No llegó
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={busyId === c.id}
                    onClick={() => setConfirmCancel(c)}
                  >
                    Cancelar
                  </Button>
                </div>
              )
            },
          },
        ]
      : []),
  ]

  return (
    <>
      <DataTable caption="Citas próximas" columns={columns} rows={citas} />
      <Modal
        open={confirmCancel !== null}
        onClose={() => setConfirmCancel(null)}
        title="Cancelar cita"
        subtitle={`Se cancelará la cita de ${confirmCancel?.patientName ?? ''}.`}
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirmCancel(null)}>
              No cancelar
            </Button>
            <Button
              variant="destructive"
              className="ml-auto"
              disabled={confirmCancel !== null && busyId === confirmCancel.id}
              onClick={() => {
                const cita = confirmCancel
                if (!cita) return
                setConfirmCancel(null)
                void act(cita.id, cancelCita, 'Cita cancelada')
              }}
            >
              Cancelar la cita
            </Button>
          </>
        }
      >
        <p className="text-sm text-text-2">
          {confirmCancel
            ? `¿Cancelar la cita de ${confirmCancel.patientName}? El paciente ya no aparecerá en la agenda.`
            : ''}
        </p>
      </Modal>
    </>
  )
}
